import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

function AIAssistant({ data }) {
    const { account } = useAuth();
    const userEmail = account?.username || 'guest@contexq.com';
    const [quota, setQuota] = useState(null);
    const [messages, setMessages] = useState([
        {
            role: 'assistant',
            content: 'Hello! I\'m your ContexQ AI Assistant powered by Gemini. I can analyze your CRM data, answer questions about deals, activities, use cases, and help you work smarter. What would you like to know?'
        }
    ]);
    const [input, setInput] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [attachment, setAttachment] = useState(null);
    const messagesEndRef = useRef(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Convert file to base64
    const handleFileSelect = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size > 10 * 1024 * 1024) {
            alert('File too large. Max size is 10MB.');
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            const base64 = reader.result.split(',')[1];
            setAttachment({
                name: file.name,
                mimeType: file.type,
                base64,
                size: file.size,
            });
        };
        reader.readAsDataURL(file);
        e.target.value = '';
    };

    const removeAttachment = () => setAttachment(null);

    // Send message to Gemini via backend
    const handleSend = async () => {
        if (!input.trim() && !attachment) return;

        const userMessage = {
            role: 'user',
            content: input,
            attachment: attachment ? { name: attachment.name } : null,
        };

        setMessages(prev => [...prev, userMessage]);
        const currentInput = input;
        const currentAttachment = attachment;
        setInput('');
        setAttachment(null);
        setIsTyping(true);

        try {
            const response = await fetch(`${API_BASE}/gemini`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    message: currentInput,
                    userEmail,
                    fileData: currentAttachment ? {
                        base64: currentAttachment.base64,
                        mimeType: currentAttachment.mimeType,
                    } : null,
                    conversationHistory: messages.filter(m => m.role !== 'system').slice(-20),
                }),
            });

            const result = await response.json();

            if (response.status === 429) {
                if (result.quota) setQuota(result.quota);
                setMessages(prev => [...prev, {
                    role: 'assistant',
                    content: `⚠️ ${result.message || 'Daily AI limit reached.'} Please try again tomorrow.`,
                }]);
                return;
            }

            if (!response.ok) {
                throw new Error(result.error || result.message || 'Failed to get response');
            }

            if (result.quota) setQuota(result.quota);
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: result.response,
            }]);
        } catch (error) {
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: `Sorry, I encountered an error: ${error.message}. Please try again.`,
            }]);
        } finally {
            setIsTyping(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    return (
        <div className="ai-assistant">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <h2 style={{ margin: 0 }}>AI Summary</h2>
                {quota && (
                    <span style={{
                        padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600,
                        background: quota.remaining === 0 ? '#fee2e2' : quota.remaining < 5 ? '#fef3c7' : '#dcfce7',
                        color: quota.remaining === 0 ? '#991b1b' : quota.remaining < 5 ? '#92400e' : '#166534',
                    }}>
                        {quota.remaining} / {quota.limit} left today
                    </span>
                )}
            </div>
            <p style={{ marginBottom: '16px', color: '#6b7280', fontSize: '14px' }}>
                Powered by Gemini — Ask questions about your CRM data, deals, activities, or attach files for analysis.
            </p>

            {/* Chat messages */}
            <div className="chat-messages">
                {messages.map((message, index) => (
                    <div key={index} className={`message ${message.role}`}>
                        {message.attachment && (
                            <div style={{ marginBottom: '8px', fontSize: '13px', color: '#6b7280' }}>
                                <span style={{ display: 'inline-block', padding: '2px 8px', background: '#f3f4f6', borderRadius: '4px' }}>
                                    📎 {message.attachment.name}
                                </span>
                            </div>
                        )}
                        <div style={{ whiteSpace: 'pre-wrap' }}>{message.content}</div>
                    </div>
                ))}
                {isTyping && (
                    <div className="message assistant">
                        <em>Gemini is thinking...</em>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Attachment preview */}
            {attachment && (
                <div style={{
                    display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px',
                    background: '#f9fafb', borderRadius: '8px', marginBottom: '8px', border: '1px solid #e5e7eb'
                }}>
                    <span style={{ fontSize: '13px' }}>📎 {attachment.name}</span>
                    <span style={{ fontSize: '11px', color: '#9ca3af' }}>({(attachment.size / 1024).toFixed(0)} KB)</span>
                    <button onClick={removeAttachment} style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: '#ef4444', fontWeight: 'bold', padding: '0 4px', marginLeft: 'auto'
                    }}>×</button>
                </div>
            )}

            {/* Input area */}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {/* File attach button */}
                <button onClick={() => fileInputRef.current?.click()} title="Attach File"
                    style={{
                        background: 'none', border: '1px solid #d1d5db', borderRadius: '8px',
                        padding: '8px 12px', cursor: 'pointer', fontSize: '16px'
                    }}>
                    📎
                </button>
                <input ref={fileInputRef} type="file" accept="image/*,.pdf,.csv,.txt,.xlsx,.docx"
                    style={{ display: 'none' }} onChange={handleFileSelect} />

                {/* Text input */}
                <input
                    type="text"
                    className="chat-input"
                    placeholder="Ask about your CRM data..."
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyPress}
                    style={{ flex: 1 }}
                />

                {/* Send button */}
                <button className="send-btn" onClick={handleSend}
                    disabled={isTyping || (!input.trim() && !attachment)}>
                    Send
                </button>
            </div>

            {/* Suggestions */}
            <div style={{ marginTop: '16px', padding: '12px', background: '#f0f9ff', borderRadius: '8px' }}>
                <strong>Try asking:</strong>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                    {[
                        "What's my pipeline status?",
                        "Show me overdue activities",
                        "Which use cases are live?",
                        "Summarize my top deals",
                        "What GTM accounts are active?",
                    ].map((q, i) => (
                        <button key={i} onClick={() => { setInput(q); }}
                            style={{
                                padding: '6px 12px', background: '#fff', border: '1px solid #d1d5db',
                                borderRadius: '16px', fontSize: '12px', cursor: 'pointer', color: '#374151'
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = '#eff6ff'}
                            onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                        >
                            {q}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default AIAssistant;
